import { Injectable, inject } from '@angular/core';
import {
  ApiClientService,
  BaseService,
  StandardApiResponse,
  StandardPagedData,
  TokenStorageService,
} from '@dlap-ui/core';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { TicketReadStateService } from './ticket-read-state.service';
import {
  normalizeTicketPage,
  requireTicketSuccess,
} from './ticket-response.util';
import { describeTicketAttachment } from './ticket-attachment.util';
import {
  CreateTicketPayload,
  OpenJiraUserRecord,
  TicketAssignee,
  TicketAttachmentDescriptor,
  TicketCreateResponse,
  TicketEscalatePayload,
  TicketMessageRecord,
  TicketRecord,
  TicketRespondPayload,
  TicketRespondResponse,
} from './tickets-api.models';

@Injectable({ providedIn: 'root' })
export class TicketsService extends BaseService {
  private readonly readState = inject(TicketReadStateService);
  private readonly tokens = inject(TokenStorageService);
  private readonly messageCountCache = new Map<
    string,
    { stamp: string; at: number; total: number; unread: number }
  >();
  private static readonly MESSAGE_COUNT_TTL_MS = 20_000;
  constructor() {
    super(inject(ApiClientService));
  }

  /**
   * Creates a support ticket through `POST /v2/support/tickets`. Without files
   * the ticket is sent as plain JSON. With files it is sent as multipart: a
   * JSON `data` part plus one `files` part per attachment, with the
   * descriptors in `data.attachments` derived from the files themselves (same
   * order as the `files` parts and the `attachmentTypes` query values).
   */
  createTicket(
    payload: CreateTicketPayload,
    files: File[] = [],
  ): Observable<StandardApiResponse<TicketCreateResponse>> {
    const url = this.buildUrl('/v2/support/tickets');

    if (!files.length) {
      return this.api
        .post<StandardApiResponse<TicketCreateResponse>>(url, {
          ...payload,
          attachments: undefined,
        })
        .pipe(map(requireTicketSuccess));
    }

    const attachments: TicketAttachmentDescriptor[] = files.map((file, index) =>
      describeTicketAttachment(
        file,
        payload.attachments?.[index]?.attachmentType,
      ),
    );

    const formData = new FormData();
    formData.append(
      'data',
      new Blob([JSON.stringify({ ...payload, attachments })], {
        type: 'application/json',
      }),
    );
    files.forEach((file) => formData.append('files', file, file.name));

    return this.api
      .postMultipart<StandardApiResponse<TicketCreateResponse>>(url, formData, {
        params: { attachmentTypes: attachments.map((a) => a.attachmentType) },
      })
      .pipe(map(requireTicketSuccess));
  }

  getTicketsByUserId(
    userId: string,
    pageNumber = 1,
    pageSize = 7,
    status = '',
    searchParam = '',
  ): Observable<StandardApiResponse<StandardPagedData<TicketRecord>>> {
    return this.api
      .get<
        StandardApiResponse<StandardPagedData<TicketRecord>>
      >(this.buildUrl(`/v2/support/tickets/user/${userId}`), { params: { pageNumber, pageSize, status: status || undefined, searchParam: searchParam || undefined } })
      .pipe(
        map(normalizeTicketPage<TicketRecord>),
        switchMap((response) => this.withMessageCounts(response)),
      );
  }

  /** The live queue currently returns zero even when incoming messages are unread. */
  protected withMessageCounts(
    response: StandardApiResponse<StandardPagedData<TicketRecord>>,
  ): Observable<StandardApiResponse<StandardPagedData<TicketRecord>>> {
    const tickets = response.data.content;
    if (!tickets.length) return of(response);
    return forkJoin(tickets.map((ticket) => this.withMessageCount(ticket))).pipe(
      map((content) => ({ ...response, data: { ...response.data, content } })),
    );
  }

  /**
   * The list endpoint does not return reliable message counts, so each ticket's
   * thread is read once and cached against the ticket's last update. Polling
   * the list then only re-reads threads whose ticket changed, instead of one
   * /messages request per row on every poll.
   */
  protected withMessageCount(ticket: TicketRecord): Observable<TicketRecord> {
    const id = ticket.id ?? ticket.ticketId;
    if (!id) return of(ticket);

    const stamp = `${ticket.updatedAt ?? ''}|${ticket.status ?? ''}`;
    const cached = this.messageCountCache.get(this.countKey(id));

    if (
      cached &&
      cached.stamp === stamp &&
      Date.now() - cached.at < TicketsService.MESSAGE_COUNT_TTL_MS
    ) {
      return of({
        ...ticket,
        totalMessagesCount: cached.total,
        unreadMessagesCount: cached.unread,
      });
    }

    return this.getTicketMessages(id).pipe(
      map((thread) => {
        const unread = this.readState.unreadCount(id, thread.data.content);

        this.messageCountCache.set(this.countKey(id), {
          stamp,
          at: Date.now(),
          total: thread.data.totalElements,
          unread,
        });

        return {
          ...ticket,
          totalMessagesCount: thread.data.totalElements,
          unreadMessagesCount: unread,
        };
      }),
      catchError(() => of(ticket)),
    );
  }

  /** Forces the next list load to re-read this ticket's thread. */
  invalidateMessageCount(ticketId: string): void {
    for (const key of [...this.messageCountCache.keys()]) {
      if (key.endsWith(`|${ticketId}`)) this.messageCountCache.delete(key);
    }
  }

  private countKey(ticketId: string): string {
    return `${this.tokens.getUser()?.id ?? 'anonymous'}|${ticketId}`;
  }

  getTicketMessages(
    ticketId: string,
  ): Observable<StandardApiResponse<StandardPagedData<TicketMessageRecord>>> {
    return this.api
      .get<
        StandardApiResponse<StandardPagedData<TicketMessageRecord>>
      >(this.buildUrl(`/v2/support/tickets/${ticketId}/messages`))
      .pipe(map(normalizeTicketPage<TicketMessageRecord>));
  }

  markTicketMessagesRead(
    ticketId: string,
    messages: TicketMessageRecord[] = [],
  ): Observable<StandardApiResponse<TicketRespondResponse>> {
    return this.api
      .post<
        StandardApiResponse<TicketRespondResponse>
      >(this.buildUrl(`/v2/support/tickets/${ticketId}/read`), {})
      .pipe(
        map(requireTicketSuccess),
        tap(() => {
          this.readState.markRead(ticketId, messages);
          this.invalidateMessageCount(ticketId);
        }),
      );
  }

  respondToTicket(
    ticketId: string,
    payload: TicketRespondPayload,
    files: File[] = [],
  ): Observable<StandardApiResponse<TicketRespondResponse>> {
    const url = this.buildUrl(`/v2/support/tickets/${ticketId}/respond`);

    if (!files.length) {
      return this.api
        .post<StandardApiResponse<TicketRespondResponse>>(url, payload)
        .pipe(
          map(requireTicketSuccess),
          tap(() => this.invalidateMessageCount(ticketId)),
        );
    }

    const attachments: TicketAttachmentDescriptor[] = files.map((file) =>
      describeTicketAttachment(file),
    );

    const formData = new FormData();
    formData.append(
      'data',
      new Blob([JSON.stringify({ ...payload, attachments })], {
        type: 'application/json',
      }),
    );
    files.forEach((file) => formData.append('files', file, file.name));

    return this.api
      .postMultipart<StandardApiResponse<TicketRespondResponse>>(url, formData, {
        params: { attachmentTypes: attachments.map((a) => a.attachmentType) },
      })
      .pipe(
        map(requireTicketSuccess),
        tap(() => this.invalidateMessageCount(ticketId)),
      );
  }

  /** Searches the external ticketing system's users for the escalate/assign pickers. */
  getOpenJiraUsers(
    pageNumber = 1,
    pageSize = 10,
    searchParam = '',
  ): Observable<StandardApiResponse<StandardPagedData<OpenJiraUserRecord>>> {
    return this.api
      .get<
        StandardApiResponse<StandardPagedData<OpenJiraUserRecord>>
      >(this.buildUrl('/v2/support/openjira/users'), {
        params: { pageNumber, pageSize, searchParam: searchParam || undefined },
      })
      .pipe(map(normalizeTicketPage<OpenJiraUserRecord>));
  }

  /** Explicitly assigns or reassigns a ticket. Responding to a ticket also assigns it to the responder on the backend. */
  assignTicket(
    ticketId: string,
    assignee: TicketAssignee,
  ): Observable<StandardApiResponse<TicketRespondResponse>> {
    return this.api
      .post<
        StandardApiResponse<TicketRespondResponse>
      >(this.buildUrl(`/v2/support/tickets/${ticketId}/assign`), assignee)
      .pipe(map(requireTicketSuccess));
  }

  /** Escalates a ticket to the external ticketing system, optionally with an assignee. */
  escalateTicket(
    ticketId: string,
    payload: TicketEscalatePayload,
  ): Observable<StandardApiResponse<TicketRespondResponse>> {
    return this.api
      .post<
        StandardApiResponse<TicketRespondResponse>
      >(this.buildUrl(`/v2/support/tickets/${ticketId}/escalate`), payload)
      .pipe(map(requireTicketSuccess));
  }
}
